import { useEffect, useState } from "react";
import { Github, LogOut, RefreshCw } from "lucide-react";
import type { ContactMessageDto, HealthState, SessionState, SiteSettings } from "../services/api";
import {
  getAdminContacts,
  getHealth,
  getSiteSettings,
  logout,
  updateContactStatus,
  updateSiteSettings,
} from "../services/api";

type AdminProps = {
  session: SessionState | null;
  refreshSession: () => Promise<void>;
};

export function Login() {
  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="login-title">
        <p className="eyebrow">Admin access</p>
        <h1 id="login-title">Miles Systems admin is GitHub-only.</h1>
        <p>Use an allowlisted GitHub account. There is no public signup path in this release.</p>
        <a className="button primary" href="/api/auth/github/start">
          <Github size={18} aria-hidden="true" />
          Continue with GitHub
        </a>
        <a className="button ghost" href="/">Back to site</a>
      </section>
    </main>
  );
}

export function Logout({ session, refreshSession }: AdminProps) {
  useEffect(() => {
    if (!session?.csrfToken) {
      return;
    }
    void logout(session.csrfToken).finally(refreshSession);
  }, [refreshSession, session?.csrfToken]);

  return (
    <main className="auth-shell">
      <section className="auth-panel">
        <h1>Signing out</h1>
        <p>The admin session is being cleared.</p>
      </section>
    </main>
  );
}

export function Admin({ session, refreshSession }: AdminProps) {
  const [contacts, setContacts] = useState<ContactMessageDto[]>([]);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [health, setHealth] = useState<HealthState | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadAdmin = async () => {
    if (!session?.authenticated) {
      return;
    }

    setError("");
    try {
      const [nextContacts, nextSettings, nextHealth] = await Promise.all([
        getAdminContacts(),
        getSiteSettings(),
        getHealth(),
      ]);
      setContacts(nextContacts);
      setSettings(nextSettings);
      setHealth(nextHealth);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Admin data could not be loaded.");
    }
  };

  useEffect(() => {
    void loadAdmin();
  }, [session?.authenticated]);

  if (!session) {
    return (
      <main className="auth-shell">
        <section className="auth-panel">
          <h1>Checking session</h1>
        </section>
      </main>
    );
  }

  if (!session.authenticated) {
    return <Login />;
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="eyebrow">Admin dashboard</p>
          <h1>Miles Systems control surface</h1>
          <p>Signed in as {session.user?.displayName ?? session.user?.githubLogin}.</p>
        </div>
        <div className="admin-actions">
          <button className="button ghost" type="button" onClick={() => void loadAdmin()}>
            <RefreshCw size={17} aria-hidden="true" />
            Refresh
          </button>
          <button
            className="button ghost"
            type="button"
            onClick={async () => {
              await logout(session.csrfToken);
              await refreshSession();
              window.history.pushState({}, "", "/login");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          >
            <LogOut size={17} aria-hidden="true" />
            Logout
          </button>
        </div>
      </header>

      {error ? <p className="admin-error" role="alert">{error}</p> : null}

      <section className="admin-grid">
        <article className="admin-panel">
          <h2>Application health</h2>
          <dl className="health-list">
            <div>
              <dt>API</dt>
              <dd>{health?.ok ? "online" : "unknown"}</dd>
            </div>
            <div>
              <dt>Database</dt>
              <dd>{health?.db.detail ?? "not checked"}</dd>
            </div>
            <div>
              <dt>Timestamp</dt>
              <dd>{health?.timestamp ?? "not checked"}</dd>
            </div>
          </dl>
        </article>

        <article className="admin-panel">
          <h2>Public metadata</h2>
          {settings ? (
            <form
              className="settings-form"
              onSubmit={async (event) => {
                event.preventDefault();
                setSaving(true);
                try {
                  setSettings(await updateSiteSettings(session.csrfToken, settings));
                } finally {
                  setSaving(false);
                }
              }}
            >
              <label>
                Status headline
                <input
                  value={settings.statusHeadline}
                  onChange={(event) => setSettings({ ...settings, statusHeadline: event.target.value })}
                />
              </label>
              <label>
                Availability
                <textarea
                  rows={3}
                  value={settings.availability}
                  onChange={(event) => setSettings({ ...settings, availability: event.target.value })}
                />
              </label>
              <label>
                Stack tags
                <input
                  value={settings.stackTags.join(", ")}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      stackTags: event.target.value
                        .split(",")
                        .map((tag) => tag.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </label>
              <button className="button primary" type="submit" disabled={saving}>
                {saving ? "Saving" : "Save metadata"}
              </button>
            </form>
          ) : (
            <p>Metadata not loaded.</p>
          )}
        </article>
      </section>

      <section className="admin-panel contact-admin-panel" aria-labelledby="admin-contact-title">
        <h2 id="admin-contact-title">Contact messages</h2>
        {contacts.length === 0 ? (
          <p>No contact messages yet.</p>
        ) : (
          <div className="contact-table" role="table" aria-label="Contact messages">
            <div className="contact-row heading" role="row">
              <span role="columnheader">Sender</span>
              <span role="columnheader">Message</span>
              <span role="columnheader">Status</span>
            </div>
            {contacts.map((contact) => (
              <div className="contact-row" role="row" key={contact.id}>
                <span role="cell">
                  <strong>{contact.name}</strong>
                  <small>{contact.email}</small>
                  {contact.company ? <small>{contact.company}</small> : null}
                </span>
                <span role="cell">{contact.message}</span>
                <span role="cell">
                  <select
                    value={contact.status}
                    aria-label={`Status for ${contact.name}`}
                    onChange={async (event) => {
                      const updated = await updateContactStatus(
                        session.csrfToken,
                        contact.id,
                        event.target.value as ContactMessageDto["status"],
                      );
                      setContacts((current) => current.map((entry) => (entry.id === updated.id ? updated : entry)));
                    }}
                  >
                    <option value="new">new</option>
                    <option value="reviewed">reviewed</option>
                    <option value="resolved">resolved</option>
                  </select>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

